import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { Carousel } from "@bloomberg/intercom";
import { SubscriberOnlyBenefitOverlay } from "@bloomberg/media-ui-subscriptions";
import { useRegUIClient } from "@foundation/reg-ui-client";

import {
  CAROUSEL_CARD_GAP,
  CAROUSEL_CARD_WIDTH,
  NUMBER_OF_STORIES_PER_TOPIC,
} from "../constants";
import { HideButton } from "../HideButton";
import { ShowButton } from "../ShowButton";
import { SingleTopicStoriesLoading } from "../SingleTopicStories";
import { trackClick } from "../tracking";
import {
  addFollowInFront,
  addFollowInPlace,
  getOptimisticTopicFollow,
  parseStoriesToExcludeFromAutofillData,
} from "../utils";
import {
  LoadingTopicCardProps,
  PanelTypes,
  SingleTopicStoriesPanelProps,
  AutofillData,
} from "../types";
import { UserTopicPanel } from "../UserTopicPanel";
import styles from "./userTopicsStoriesMobile.module.css";
import {
  DrawerTogglePicker,
  useDrawerTogglePicker,
} from "~components/TopicModule/DrawerTogglePicker";
import intercom from "~config/intercom";
import { useTopicsStories } from "~hooks/useTopicsStories";
import { addPageAction, logError } from "~utils/newrelic/newrelicBrowser";

const TRACKING_ID = "[lineup]topic_module";

type UserTopicsStoriesMobileProps = {
  autofillData?: AutofillData;
};

export const UserTopicsStoriesMobile = ({
  autofillData,
}: UserTopicsStoriesMobileProps) => {
  const { client } = useRegUIClient();
  const bconnectId = useRegUIClient().session?.userInfo?.userId ?? "";
  const isSignedIn = Boolean(bconnectId);
  const showTopicsRef = useRef<HTMLButtonElement | null>(null);
  const [isSubscriberBenefitOpen, setIsSubscriberBenefitOpen] = useState(false);

  const drawerTogglePickerHook = useDrawerTogglePicker();

  const { setShowDrawerTopicsPanel, showDrawerTopicsPanel } =
    drawerTogglePickerHook;

  useEffect(() => {
    // This is for preventing scroll on body content when the topic drawer is open
    document.body.style.overflowY = showDrawerTopicsPanel ? "hidden" : "auto";
  }, [showDrawerTopicsPanel]);

  const storyIdsToExclude = autofillData
    ? parseStoriesToExcludeFromAutofillData(autofillData)
    : "";

  const {
    topicsError,
    topicsStoriesError,
    isLoading,
    setTopics,
    topics,
    topicsStories,
  } = useTopicsStories({ bconnectId, storyIdsToExclude });

  const followTopicCallback = useCallback(
    async (topicId: string, addInPlace?: boolean) => {
      addPageAction("followTopic", {
        topicId,
      });
      if (bconnectId) {
        flushSync(() => {
          setTopics((currentTopics) =>
            addInPlace
              ? addFollowInPlace(
                  currentTopics,
                  getOptimisticTopicFollow(topicId)
                )
              : addFollowInFront(
                  currentTopics,
                  getOptimisticTopicFollow(topicId)
                )
          );
        });
        try {
          const isTopicFollowSuccess = await intercom.followTopic({
            topicId,
          });

          if (isTopicFollowSuccess) {
            trackClick("follow", `${topicId}_topicpanel`);
          }
        } catch (e) {
          console.warn(e);
          trackClick("unsuccessful follow", `${topicId}_topicpanel`);
          logError(new Error("[followTopic] - error following topic"), {
            bconnectId,
            topicId,
          });

          flushSync(() =>
            setTopics((currentTopics) =>
              currentTopics.map((currentTopic) =>
                currentTopic.id === topicId
                  ? {
                      ...currentTopic,
                      isFollowedByUser: false,
                    }
                  : currentTopic
              )
            )
          );
        }
      }
    },
    [bconnectId, setTopics]
  );

  const unfollowTopicCallback = useCallback(
    async (topicId: string) => {
      addPageAction("unFollowTopic", {
        topicId,
      });
      if (bconnectId) {
        flushSync(() =>
          setTopics((currentTopics) =>
            currentTopics.filter((currentTopic) => currentTopic.id !== topicId)
          )
        );
        try {
          const isTopicUnfollowSuccess = await intercom.deleteTopic({
            topicId,
          });

          if (isTopicUnfollowSuccess) {
            trackClick("unfollow", `${topicId}_topicpanel`);
          }
        } catch (e) {
          console.warn(e);
          trackClick("unsuccessful unfollow", `${topicId}_topicpanel`);
          // logError(new Error("[unfollowTopic] - error unfollowing topic"), {
          //   bconnectId,
          //   topicId,
          // });

          flushSync(() =>
            setTopics((currentTopics) =>
              addFollowInFront(currentTopics, getOptimisticTopicFollow(topicId))
            )
          );
        }
      }
    },
    [bconnectId, setTopics]
  );

  const onShowTopicPickerInDrawer = useCallback(() => {
    if (bconnectId) {
      trackClick("toggle_topic_picker", "picker_open");
      setShowDrawerTopicsPanel(true);
    } else {
      setIsSubscriberBenefitOpen(true);
    }
  }, [setShowDrawerTopicsPanel, bconnectId]);

  const onHideTopicPickerInDrawer = useCallback(() => {
    trackClick("toggle_topic_picker", "picker_close");
    setShowDrawerTopicsPanel(false);
  }, [setShowDrawerTopicsPanel]);

  const onTopicPickerDrawerExit = useCallback(() => {
    trackClick("toggle_topic_picker", "picker_close");
  }, []);

  const handleOnExit = useCallback(() => {
    setIsSubscriberBenefitOpen(false);
  }, [setIsSubscriberBenefitOpen]);

  const mobileComponentPropsList: (
    | SingleTopicStoriesPanelProps
    | LoadingTopicCardProps
  )[] = useMemo(
    () =>
      topics.map((topic) => {
        if (!topicsStories[topic.id]) {
          return {
            type: PanelTypes.LoadingTopicCard,
            props: {},
          };
        }

        return {
          type: PanelTypes.TopicCard,
          props: {
            topic,
            stories: topicsStories[topic.id],
            followTopicCallback,
            isSignedIn,
          },
        };
      }),
    [topics, topicsStories, followTopicCallback, isSignedIn]
  );

  if (topicsError) {
    console.error(
      `[UserTopicsStoriesMobile] An error occurred fetching topics: ${topicsError}`
    );
  }

  if (topicsStoriesError) {
    console.error(
      `[UserTopicsStoriesMobile] An error occurred fetching topicsStories: ${topicsStoriesError}`
    );
  }

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <SingleTopicStoriesLoading
          numberOfRows={NUMBER_OF_STORIES_PER_TOPIC}
          length={2}
        />
      </div>
    );
  }

  if (!topicsStories || !topics) {
    return null;
  }

  return (
    <div className={styles.userTopicsStoriesMobile}>
      <div className={styles.header}>
        <h3 className={styles.title}>Market News</h3>
        {showDrawerTopicsPanel ? (
          /** TODO [MEDIAUI-151]: replace with newer button in lineup when new token designs are available */
          <HideButton onClick={onHideTopicPickerInDrawer} />
        ) : (
          <ShowButton onClick={onShowTopicPickerInDrawer} ref={showTopicsRef} />
        )}
        {isSubscriberBenefitOpen && (
          <SubscriberOnlyBenefitOverlay
            anchorElementRef={showTopicsRef}
            client={client!}
            inSource="homepage-topic-module"
            onExit={handleOnExit}
          >
            Sign in to follow topics and surface the content most relevant to
            you
          </SubscriberOnlyBenefitOverlay>
        )}
      </div>
      <div className={styles.mobileCarouselContainer}>
        <Carousel.WithTracking
          IterableComponent={UserTopicPanel}
          componentPropsList={mobileComponentPropsList}
          tileProperties={{
            gap: CAROUSEL_CARD_GAP,
            width: CAROUSEL_CARD_WIDTH,
          }}
          trackingId={TRACKING_ID}
          disableMouseDragScroll
        />
      </div>
      <DrawerTogglePicker
        drawerTogglePickerHook={drawerTogglePickerHook}
        topics={topics}
        unfollowTopicCallback={unfollowTopicCallback}
        followTopicCallback={followTopicCallback}
        onClose={onTopicPickerDrawerExit}
        isSignedIn={isSignedIn}
      />
    </div>
  );
};
